import React, { useEffect } from 'react';
import moment from 'moment';

import useTestTypeFeatures from 'src/shared/hooks/useTestTypeFeatures';
import { scrollToElementFromHash } from 'src/shared/utils/scrollToElement';

import { ErrorMessageWithBackground } from 'src/shared/components/error-msg/ErrorMessage';

import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import FishMetrics from './fish-metrics/FishMetrics';
import SpeciesRichnessMetrics from './species-richness-metrics/SpeciesRichnessMetrics';
import MetricsTable from './metrics-table/MetricsTable';
import EvolutionaryMetrics from './evolutionary-metrics/EvolutionaryMetrics';
import BacterialFunctionalDiversityMetrics from './bacterial-functional-diversity/BacterialFunctionalDiversityMetrics';
import SpeciesHighlight from './species-highlight/SpeciesHighlight';
import FungalFunctionalDiversityMetrics from './fungal-functional-diversity/FungalFunctionalDiversityMetrics';
import styles from './Metrics.module.scss';
import FungalToBacterialRatioMetrics from './fungal-to-bacterial-ratio/FungalToBacterialRatioMetrics';
import MarineSedimentPollutionIndexMetrics from './marine-sediment-pollution-index-metrics/MarineSedimentPollutionIndexMetrics';
import SpeciesGroupsMetrics from './species-groups-metrics/SpeciesGroupsMetrics';
import SingleTimepointMetricsOverview from '../single-timepoint-metrics-overview/SingleTimepointMetricsOverview';
import TotalSpeciesMetrics from '../metrics/total-species-metrics/TotalSpeciesMetrics';
import InvasiveMetrics from '../metrics/invasive-metrics/InvasiveMatrics';
import IucnMetrics from '../metrics/iucn-metrics/IucnMetrics';

export const Metrics = () => {
    const { currentProjectSelectedHabitatAssayType: habitatAssayType, currentProjectSelectedDateRange } = useProjectFilters();
    const {
        hasSpeciesRichnessFeature,
        hasEvolutionaryDiversityFeature,
        hasIucnRedListFeature,
        hasInvasivesFeature,
        hasFishMetricsFeature,
        hasBfdFeature,
        hasFfdFeature,
        hasSpeciesHighlightFeature,
        hasChegdFeature,
        hasFbRatioFeature,
        hasMspiFeature,
        hasSpeciesGroupsFeature,
        hasTotalSpeciesFeature,
    } = useTestTypeFeatures();
    const isSingleTimepoint = habitatAssayType?.singleTimepoint || false;

    const speciesHighlight = hasSpeciesHighlightFeature && (
        <SpeciesHighlight isFishMetricsPresent={hasFishMetricsFeature} isSingleTimepoint={isSingleTimepoint} />
    );

    const iucnAndInvasivesMetrics = (hasIucnRedListFeature || hasInvasivesFeature || hasChegdFeature) && (
        <div className={styles.iucnMetrics} data-testid='iucn-and-invasives-metrics'>
            <MetricsTable />
        </div>
    );

    const fishMetric = hasFishMetricsFeature && (
        <div className={styles.fishIndex}>
            <FishMetrics isSingleTimepoint={isSingleTimepoint} />
        </div>
    );

    const otherMetrics = (
        <div data-testid='other-metrics-container'>
            {hasFishMetricsFeature ? (
                <>
                    {!isSingleTimepoint ? (
                        <div className={styles.fishMetricAndSpeciesHighlight}>
                            {fishMetric}
                            {speciesHighlight}
                        </div>
                    ) : (
                        <>
                            {fishMetric}
                            {speciesHighlight}
                        </>
                    )}
                    {iucnAndInvasivesMetrics}
                </>
            ) : (
                <>
                    {iucnAndInvasivesMetrics}
                    {speciesHighlight}
                </>
            )}
        </div>
    );

    const selectedFromDate = moment(currentProjectSelectedDateRange?.startDate);
    const selectedToDate = moment(currentProjectSelectedDateRange?.endDate);

    // Scroll to specific metrics table based on hash in URL
    useEffect(() => {
        scrollToElementFromHash();
    }, []);
    return (
        <div className={styles.metricsContainer} data-testid='metrics-container'>
            {selectedToDate.isBefore(selectedFromDate) && (
                <ErrorMessageWithBackground
                    message={`Adjust the date range above so the 'Displaying from' date is earlier than the 'to' date.`}
                    testId='date-range-error-message'
                />
            )}
            {isSingleTimepoint && <SingleTimepointMetricsOverview />}
            {!isSingleTimepoint && (
                <div className={styles.metrics}>
                    {hasTotalSpeciesFeature && <TotalSpeciesMetrics />}
                    {hasSpeciesRichnessFeature && <SpeciesRichnessMetrics />}
                    {hasEvolutionaryDiversityFeature && <EvolutionaryMetrics />}
                    {hasIucnRedListFeature && <IucnMetrics />}
                    {!hasChegdFeature && hasInvasivesFeature && <InvasiveMetrics />}
                    {hasBfdFeature && <BacterialFunctionalDiversityMetrics />}
                    {hasFfdFeature && <FungalFunctionalDiversityMetrics />}
                    {hasFbRatioFeature && <FungalToBacterialRatioMetrics />}
                    {hasMspiFeature && <MarineSedimentPollutionIndexMetrics />}
                </div>
            )}
            {hasSpeciesGroupsFeature && <SpeciesGroupsMetrics />}
            {otherMetrics}
        </div>
    );
};

export default Metrics;
